import countries from "./countries.json";
import states from "./states.json";
import counties from "./counties.json";
import addressComponents from "./addressComponents.js";
import countiesToState from "./counties-states.json";

import { postalCodeRegexByCountry } from "./postalCodes.js";

/**
 * Returns a map of all supported countries in the form:
 * {"US": "United States Of America", ...}
 * or
 * {"IT": "Italia", ...}
 * depending on the language.
*/
export const getCountryMap = ({ language }) => {
  return Object.keys(countries).reduce((acc, shortCode) => {
    const cntry = countries[shortCode];
    acc[shortCode] = cntry[`alt_${language}`] || cntry.default;
    return acc;
  }, {});
}

/**
 * Returns true if "country" is either a country short code (e.g. "US") or long form (e.g. "United States of America")
 */
export const isValidCountry = (country) => {
  if (countries[country?.toUpperCase()]) return true;
  for (const c in countries) {
    if (Object.values(countries[c]).includes(country)) {
      return true;
    }
  }
  return false;
};

export const getStatesForCountry = ({ country: countryShortCode }) => states[countryShortCode || 'US'];

/** Returns long form of a State; if the State is an object, looks for the name in the given language or the default name
  e.g. for Spain:
      "CT": {
         "default": "Cataluña",
         "alt_ca": "Catalunya",
         "alt_en": "Catalonia"
      },
*/
export const getStateLongForCountry = ({country, state, language }) => {
  const states = getStatesForCountry({country: country || 'US'});
  const stateObjOrString = states?.[state];
  if(typeof stateObjOrString === 'string') {
    return stateObjOrString;
  } else if (typeof stateObjOrString === 'object'){
    return stateObjOrString[`alt_${language}`] ?? stateObjOrString.default;
  } else {
    return null;
  }
}

export const getCountiesForCountry = ({country: countryShortCode}) => counties[countryShortCode || 'US'];

export const getCountyLongForCountry = ({country, county, language }) => {
  const counties = getCountiesForCountry({country});
  const countyObjOrString = counties?.[county];
  if(typeof countyObjOrString === 'string') {
    return countyObjOrString;
  } else if (typeof countyObjOrString === 'object'){
    return countyObjOrString[`alt_${language}`] ?? countyObjOrString.default;
  }
}

/**
 * Returns true if for a given "country", "state" is either a state short code (e.g. "AL" for country "US") or long form (e.g. "Alabama")
 */
export const isValidState = (country, state) => {
  if (!isValidCountry(country)) return false;

  const countryShortCode = getCountryAbbreviation(country);

  const statesForCountry = getStatesForCountry({country: countryShortCode});
  if (statesForCountry[state]) return true;
  if (Object.values(statesForCountry).includes(state)) {
    return true;
  }
  for(const shortState in statesForCountry) {
    // shortState: C for Connacht
    for(const k in statesForCountry[shortState]) {
      // k: "default", "alt_en", ...
      if(statesForCountry[shortState]?.[k] === state) return true;
    }
  }
  return false;
};

/**
 * Returns true if for a given "country", "county" is either a county short code (e.g. "MLN" for country "GB") or long form (e.g. "Midlothian")
 */
export const isValidCounty = (country, county) => {
  if (!isValidCountry(country)) return false;

  let countiesForCountry = counties[country];
  const alias = countiesForCountry["_alias"];
  if (alias) {
    // e.g. "UK" is an alias of "GB"
    countiesForCountry = counties[alias];
  }
  if (countiesForCountry[county]) return true;

  const otherName = getCountrySpecificComponentName(country, 'county');
  if (countiesForCountry[otherName]) return true;
  if (Object.values(countiesForCountry).includes(county)) {
    return true;
  }
  return false;
};

/**
 * Returns true if for a given "country", "county" is valid relative to "state", e.g. "MLN"("Midlothian") is a county in state "SCO" ("Scotland") for country "GB".
 * If no mapping is defined between counties and states, will return true.
 */
export const isValidCountyForState = (country, county, state) => {
  if (!isValidCountry(country)) return false;
  if(!state) return true;
  return countiesToState[country]?.[county] === state;
}

/**
 * Returns the short codes of counties that belong to "state".
*/
export const getCountiesForState = (country, state) => {
  if (!isValidCountry(country)) return false;
  const counties = Object.keys(getCountiesForCountry({country, state}) ?? {});
  return counties.filter(county => countiesToState[country]?.[county] === state);
}

export const isValidComponent = (country, value, subDivision) => {
  if (!isValidCountry(country)) return false;

  const internalComponent = getInternalComponentName(country, subDivision);
  switch(internalComponent) {
    case 'state': return isValidState(country, value);
    case 'county': return isValidCounty(country, value);
    // case 'region': return;
    // case 'province': return;
    default: return false;
  }
}


/**
 * For a given "country" as long or short form, return the short form. (e.g. "Great Britain"/"GB" => "GB"
*/
export const getCountryAbbreviation = (country) => {
  if (countries[country]) return country;
  for(const countryShortCode in countries) {
    for(const k in countries[countryShortCode]) {
      // k: "default", "alt_en", ...
      if(countries[countryShortCode][k] === country) return countryShortCode;
    }
  }
};

/**
 * For a given "country" in short form, return the long form
 */
export const getCountryLongForm = ({country, language = 'en'}) => {
  const countryObj = countries?.[country];
  return countryObj?.[`alt_${language}`] ?? countryObj?.default;
};

/**
 * Returns the unit (imperial or metric) for the country and measurement. If no
 * specific measurement is given (e.g. "speed", "temperature"), or the country
 * doesn't have this measurement defined, returns "metric".
*/
export const getCountryUnit = ({country, measurement}) => {
  const countryObj = countries?.[country || 'US'];
  if(!countryObj) return 'metric';
  const all = countryObj?.['unit_all']; // 'override' value so defining separate measurements are not needed
  if(all) return all;
  return countryObj[`unit_${measurement}`] ?? "metric";
}

/**
 * Returns an object of the units (imperial or metric) for the country by
 * measurement. If country not defined, 'metric' for all measurements.
 * e.g. getCountryUnits({country: 'UK'})
 * -> { speed: "imperial", temperature: "metric", precipitation: "metric" }
*/
export const getCountryUnits = ({country}) => {
  const countryObj = countries?.[country];
  if(!countryObj) return {
    speed: 'metric',
    temperature: 'metric',
    precipitation: 'metric',
    distance: 'metric',
    // pressure? would be hPa, mbar, ... instead of metric/imperial
  };
  const all = countryObj['unit_all']; // 'override' value so defining separate measurements are not needed
  if(all) return {
    speed: all,
    temperature: all,
    precipitation: all,
    distance: all,
    // pressure? would be hPa, mbar, ... instead of metric/imperial
  };
  return {
    speed: countryObj[`unit_speed`] ?? "metric",
    temperature: countryObj[`unit_temperature`] ?? "metric",
    precipitation: countryObj[`unit_precipitation`] ?? "metric",
    distance: countryObj[`unit_distance`] ?? "metric",
    // pressure? would be hPa, mbar, ... instead of metric/imperial
  };
}

export const getCountryDateTimeFormat = ({country}) => {
  const countryObj = countries?.[country];
  if(!countryObj) return {
    dateFmt: "dmy",
    timeFmt: "24hr"
  };
  return {
    dateFmt: countryObj["fmt_date"] ?? "dmy",
    timeFmt: countryObj["fmt_time"] ?? "24hr",
  }
}

export const isValidPostalCode = (country, postalCode) => {
  const countryCode = getCountryAbbreviation(country);
  const reg = postalCodeRegexByCountry(countryCode);
  if(Array.isArray(reg)) {
    for(const r of reg) {
      const match = postalCode.match(r);
      return match ? true : false;
    }
  } else {
    const match = postalCode.match(reg);
    return match ? true : false;
  }
}

const checkCountry = (countryCmp) => {
  if(!countryCmp) {
    console.log("checkCountry ERROR: unknown country ", countryCmp);
    return false;
  } else {
    return true;
  }
}

/**
 * Checks if country is defined and the component has an object with key
 * "required" and value "true"; if it is marked "eitherAlternative", then either the
  * given component or another component is required.
  * if "internal" is false, then convert 'component' to a country specific component first, e.g. in Italy, the FMS 'county' is equivalent to the Italian 'province'
  * Return value:
  * true or false
  * Example 1: AU has "city" or "suburb" as "eitherAlternative", so the return value would be: true
  * Example 2: CA has "city" as required: true
  * Example 3: CA has no 'required' key under "zipCode": false
  *
*/
export const isRequiredComponent = (country, component, internal = false) => {
  const countryCmp = addressComponents.byCountry[country];
  if(!checkCountry(countryCmp)) return null;

  const cmpKey = internal ? component : getInternalComponentName(country, component);

  // if key not applicable to country, it's not required
  // if(!countryCmp?.[cmpKey]?.required) return false;

  const cmpVal = countryCmp[cmpKey];
  if(!cmpVal) {
    // component is not defined for country, so is not required
    return false;
  } else if(cmpVal.required) {
    return true;
  } else if(cmpVal.eitherAlternative) {
    // if eitherAlternative is defined, it's is always required
    // const alternativeVal = countryCmp[cmpVal.eitherAlternative];
    if(!countryCmp[cmpVal.eitherAlternative]) {
      console.log("isRequiredComponent ERROR: eitherAlternative '%s' is not defined for country '%s'", cmpVal.eitherAlternative, country);
    }
    return true;
  } else {
    return false;
  }
}

/**
 * Collects an array with components that are marked required:true.
*/
export const getRequiredComponents = ({country, internal = false}) => {
  const countryCmp = addressComponents.byCountry[country];
  if(!checkCountry(countryCmp)) return [];
  return Object.keys(countryCmp).filter((cmp) => countryCmp[cmp]?.required);
}

/**
 * Translate from internal subdivision keys to those that are in line with
 * country local usage; e.g. Belgium has 3 "region"s which is used internally
  * as "state" (subdivision 1) and 10 "province"s, which are internally
  * "county" (subdivision 2).
  * "!county": "=province" means FMS county is Belgian province
  * If no alternatives are defined, then just returns the initial component.
*/
export const getCountrySpecificComponentName = (country, component) => {
  const countryCmp = addressComponents.byCountry[country];
  if(!checkCountry(countryCmp)) return null;

  if(!countryCmp[component]) {
    return null;
  }
  const countryKey = countryCmp[component].countryKey;
  if(countryKey) {
    if(!countryCmp[countryKey]) {
      // double check that the alias is defined
      console.log("getCountrySpecificComponentName ERROR: countryKey '%s' is not defined for component '%s' and country '%s'", countryKey, component, country);
      return null;
    }

    return countryKey;
  } else {
    return component;
  }

}

// reverse of getCountrySpecificComponentNames()
// e.g. for Belgium, map from "province" to "county"
export const getInternalComponentName = (country, component) => {
  const countryCmp = addressComponents.byCountry[country];
  if(!checkCountry(countryCmp)) {
    console.log("no countryCmp for country %s  component %s", country, component);
    return null;
  }

  if(!countryCmp[component]) return component;

  const fmsKey = countryCmp[component].fmsKey;
  if(fmsKey) {
    if(!countryCmp[fmsKey]) {
      // double check that the alias is defined
      console.log("getInternalComponentName ERROR: fmsKey '%s' is not defined for component '%s' and country '%s'", fmsKey, component, country);
      return null;
    }

    return fmsKey;
  } else {
    return component;
  }
}

